import React from 'react';
import Link from 'next/link';

/**
 * One button system for the whole site.
 *
 * Before this, the codebase had ~14 distinct button class strings with different
 * heights, radii, casing and tracking. Every call site now picks a variant.
 *
 * Deliberate choices:
 *  - Sentence case, not ALL CAPS with wide tracking. Uppercase buttons read as
 *    consumer marketing; sentence case reads as technical product UI.
 *  - Fixed heights per size so a row of mixed buttons always aligns.
 *  - Movement is limited to a 1px press. No lift, no glow, no gradient.
 */

type Variant = 'primary' | 'secondary' | 'dark' | 'light' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-semibold whitespace-nowrap ' +
  'transition-[background-color,border-color,color,box-shadow,transform] duration-fast ease-out ' +
  'active:translate-y-px active:scale-[0.99] motion-reduce:active:transform-none ' +
  'disabled:pointer-events-none disabled:opacity-50';

const variants: Record<Variant, string> = {
  // Blue. The single strongest action on a light surface.
  primary:
    'bg-accent text-accent-foreground shadow-[0_1px_2px_rgba(2,132,199,0.35),inset_0_1px_0_rgba(255,255,255,0.18)] hover:bg-accent-strong hover:shadow-[0_4px_14px_rgba(2,132,199,0.35)]',
  // Outlined on light surfaces.
  secondary:
    'border border-border bg-background text-foreground hover:border-foreground/30 hover:bg-surface-2',
  // Navy. Used where blue would compete with an adjacent primary.
  dark: 'bg-primary text-primary-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] hover:bg-[#26344d] hover:shadow-md',
  // On dark/photographic surfaces.
  light:
    'border border-white/30 bg-white/[0.03] text-white backdrop-blur-[2px] hover:border-white/60 hover:bg-white/10',
  ghost: 'text-accent-strong hover:bg-accent/10',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3.5 text-[0.8125rem]',
  md: 'h-11 px-5 text-sm',
  lg: 'h-12 px-6 text-[0.9375rem]',
};

type CommonProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: React.ReactNode;
};

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}: CommonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function ButtonLink({
  href,
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  external,
  ...rest
}: CommonProps & { href: string; external?: boolean } & Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    'href' | 'className' | 'children'
  >) {
  const cls = `${base} ${variants[variant]} ${sizes[size]} ${className}`;

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={cls} {...rest}>
      {children}
    </Link>
  );
}

/**
 * Inline text link with an arrow that nudges 2px on hover — the site's standard
 * "read more" affordance, replacing the repeated full "Learn More" buttons that
 * previously sat on every card.
 */
export function ArrowLink({
  href,
  children,
  tone = 'light',
  className = '',
}: {
  href: string;
  children: React.ReactNode;
  tone?: 'light' | 'dark';
  className?: string;
}) {
  return (
    <Link
      href={href}
      className={`group inline-flex items-center gap-1.5 text-sm font-semibold transition-colors duration-fast ${
        tone === 'dark'
          ? 'text-accent hover:text-white'
          : 'text-accent-strong hover:text-foreground'
      } ${className}`}
    >
      {children}
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        className="transition-transform duration-fast ease-out group-hover:translate-x-0.5 motion-reduce:transform-none"
      >
        <path
          d="M6 3.5L10.5 8L6 12.5"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Link>
  );
}
